import { ApolloError } from "@apollo/client";
import { USER_FACING_ERROR_CODE } from "encamp-shared/src/constants/graphqlErrorCodes";

/**
 * Returns the first `extensions.code` from an Apollo error's GraphQL errors,
 * or undefined if the error isn't an ApolloError or doesn't carry a code.
 * Useful for branching UI on server-side error codes like SNAPSHOT_PENDING.
 */
export function getGraphQLErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApolloError)) return undefined;
  for (const gqlError of error.graphQLErrors ?? []) {
    const code = gqlError.extensions?.code;
    if (typeof code === "string" && code.length > 0) return code;
  }
  return undefined;
}

/**
 * Checks if an error is an authorization/permission error
 */
export function isAuthorizationError(error: unknown): boolean {
  return getGraphQLErrorCode(error) === "FORBIDDEN";
}

/**
 * The server's message for an error carrying one of `codes`, else undefined.
 *
 * **Keyed on `extensions.code`, deliberately, not on the `userFacing` marker.**
 * `sanitizeGraphQLError` classifies with that marker and then *deletes* it
 * before the response leaves the server, so a client reading it gets `undefined`
 * in release while working fine in dev and in every test. `code` survives, and
 * the message itself is kept verbatim for expected client errors — so naming the
 * codes whose wording matters is the only thing that actually works in
 * production.
 */
export function getMessageForErrorCodes(error: unknown, codes: string[]): string | undefined {
  if (!(error instanceof ApolloError)) return undefined;
  for (const gqlError of error.graphQLErrors ?? []) {
    const code = gqlError.extensions?.code;
    if (typeof code === "string" && codes.includes(code) && gqlError.message.length > 0) {
      return gqlError.message;
    }
  }
  return undefined;
}

/**
 * The server's verbatim message for a UserFacingError, or undefined. Surfaces
 * business/validation refusals — including the archived-facility read-only
 * message — that a generic catch would otherwise swallow. Keyed on the code,
 * which (unlike the `userFacing` marker) survives release sanitization.
 */
export function getUserFacingMessage(error: unknown): string | undefined {
  return getMessageForErrorCodes(error, [USER_FACING_ERROR_CODE]);
}
